import React, { useState, useEffect } from 'react';
import { supabase } from '../../utils/supabaseClient';
import Notification from '../common/Notification';
import { FaArrowUp, FaCheckCircle, FaTimesCircle, FaUsers, FaSpinner, FaChevronRight } from 'react-icons/fa';
import '../../styles/StudentPromotion.css';


function StudentPromotion() {
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [students, setStudents] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState(new Set());
  const [targetClass, setTargetClass] = useState('');
  const [loading, setLoading] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const [notification, setNotification] = useState(null);
  const [promotionResult, setPromotionResult] = useState(null);

  // Fetch available classes on mount
  useEffect(() => {
    fetchClasses();
  }, []);

  // Fetch students when class is selected
  useEffect(() => {
    if (selectedClass) {
      fetchStudentsByClass(selectedClass);
      // Auto-set target class to next in the list
      const currentIndex = classes.indexOf(selectedClass);
      if (currentIndex >= 0 && currentIndex < classes.length - 1) {
        setTargetClass(classes[currentIndex + 1]);
      } else {
        setTargetClass('');
      }
    } else {
      setStudents([]);
      setSelectedStudents(new Set());
      setTargetClass('');
    }
    setPromotionResult(null);
  }, [selectedClass, classes]);

  const fetchClasses = async () => {
    try {
      const { data, error } = await supabase
        .from('classes')
        .select('class_name')
        .order('id', { ascending: true });

      if (error) throw error;

      const classList = data.map(d => d.class_name).filter(Boolean);
      setClasses(classList);
    } catch (error) {
      console.error('Error fetching classes:', error);
      showNotification('Failed to load classes', 'error');
    }
  };

  const fetchStudentsByClass = async (classValue) => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('students')
        .select('Rno, Name, Gender, Class, Active')
        .eq('Class', classValue)
        .eq('Active', true)
        .order('Rno', { ascending: true });

      if (error) throw error;
      setStudents(data || []);
      setSelectedStudents(new Set());
    } catch (error) {
      console.error('Error fetching students:', error);
      showNotification('Failed to load students', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (message, type) => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000);
  };

  const toggleStudent = (rno) => {
    setSelectedStudents(prev => {
      const next = new Set(prev);
      if (next.has(rno)) {
        next.delete(rno);
      } else {
        next.add(rno);
      }
      return next;
    });
  };

  const selectAll = () => {
    if (selectedStudents.size === students.length) {
      setSelectedStudents(new Set());
    } else {
      setSelectedStudents(new Set(students.map(s => s.Rno)));
    }
  };

  const handlePromote = async () => {
    if (selectedStudents.size === 0) {
      showNotification('Please select at least one student', 'error');
      return;
    }
    if (!targetClass) {
      showNotification('Please select target class', 'error');
      return;
    }
    if (targetClass === selectedClass) {
      showNotification('Target class must be different from current class', 'error');
      return;
    }

    setPromoting(true);
    setPromotionResult(null);

    try {
      const studentRnos = Array.from(selectedStudents);
      
      const { data, error } = await supabase
        .from('students')
        .update({ Class: targetClass })
        .in('Rno', studentRnos)
        .select();

      if (error) throw error;

      const successCount = data?.length || 0;
      const failCount = studentRnos.length - successCount;

      setPromotionResult({
        success: successCount,
        failed: failCount,
        total: studentRnos.length,
        fromClass: selectedClass,
        toClass: targetClass
      });

      showNotification(
        `Successfully promoted ${successCount} student${successCount !== 1 ? 's' : ''} to ${targetClass}`,
        'success'
      );

      // Refresh the student list
      fetchStudentsByClass(selectedClass);

    } catch (error) {
      console.error('Error promoting students:', error);
      showNotification('Failed to promote students: ' + error.message, 'error');
      setPromotionResult({
        success: 0,
        failed: selectedStudents.size,
        total: selectedStudents.size,
        fromClass: selectedClass,
        toClass: targetClass
      });
    } finally {
      setPromoting(false);
    }
  };

  const getAvailableTargetClasses = () => {
    return classes.filter(c => c !== selectedClass);
  };

  return (
    <div className="student-promotion-container">
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}

      <div className="promotion-header">
        <div className="promotion-header-icon">
          <FaArrowUp />
        </div>
        <div>
          <h2>Student Promotion</h2>
          <p>Promote students to the next class</p>
        </div>
      </div>

      {/* Step 1: Select Source Class */}
      <div className="promotion-step">
        <div className="step-number">1</div>
        <div className="step-content">
          <h3>Select Current Class</h3>
          <select
            value={selectedClass}
            onChange={(e) => setSelectedClass(e.target.value)}
            className="promotion-select"
            disabled={promoting}
          >
            <option value="">-- Select Class --</option>
            {classes.map(cls => (
              <option key={cls} value={cls}>{cls}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Step 2: Select Students */}
      {selectedClass && (
        <div className="promotion-step">
          <div className="step-number">2</div>
          <div className="step-content">
            <div className="step-header">
              <h3>Select Students</h3>
              <div className="selection-info">
                <span className="selected-count">
                  <FaUsers /> {selectedStudents.size} / {students.length} selected
                </span>
                <button
                  className="select-all-btn"
                  onClick={selectAll}
                  disabled={promoting || students.length === 0}
                >
                  {selectedStudents.size === students.length && students.length > 0 ? 'Deselect All' : 'Select All'}
                </button>
              </div>
            </div>

            {loading ? (
              <div className="promotion-loading">
                <FaSpinner className="spin" />
                <span>Loading students...</span>
              </div>
            ) : students.length === 0 ? (
              <div className="no-students">
                No active students found in {selectedClass}
              </div>
            ) : (
              <div className="students-grid">
                {students.map(student => (
                  <div
                    key={student.Rno}
                    className={`student-card ${selectedStudents.has(student.Rno) ? 'selected' : ''}`}
                    onClick={() => !promoting && toggleStudent(student.Rno)}
                  >
                    <div className="student-checkbox">
                      {selectedStudents.has(student.Rno) ? (
                        <FaCheckCircle className="checked" />
                      ) : (
                        <div className="unchecked" />
                      )}
                    </div>
                    <div className="student-info">
                      <span className="student-name">{student.Name}</span>
                      <span className="student-rno">Roll No: {student.Rno}</span>
                    </div>
                    <span className={`student-gender ${student.Gender?.toLowerCase()}`}>
                      {student.Gender === 'Male' ? 'M' : 'F'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Step 3: Select Target Class & Promote */}
      {selectedClass && students.length > 0 && (
        <div className="promotion-step">
          <div className="step-number">3</div>
          <div className="step-content">
            <h3>Promote To</h3>
            <div className="promote-action">
              <div className="class-flow">
                <div className="class-badge current">{selectedClass}</div>
                <FaChevronRight className="flow-arrow" />
                <select
                  value={targetClass}
                  onChange={(e) => setTargetClass(e.target.value)}
                  className="promotion-select target-select"
                  disabled={promoting}
                >
                  <option value="">-- Target Class --</option>
                  {getAvailableTargetClasses().map(cls => (
                    <option key={cls} value={cls}>{cls}</option>
                  ))}
                </select>
              </div>

              <button
                className="promote-btn"
                onClick={handlePromote}
                disabled={promoting || selectedStudents.size === 0 || !targetClass}
              >
                {promoting ? (
                  <>
                    <FaSpinner className="spin" />
                    Promoting...
                  </>
                ) : (
                  <>
                    <FaArrowUp />
                    Promote {selectedStudents.size} Student{selectedStudents.size !== 1 ? 's' : ''}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Promotion Result */}
      {promotionResult && (
        <div className={`promotion-result ${promotionResult.failed === 0 ? 'success' : 'partial'}`}>
          <div className="result-icon">
            {promotionResult.failed === 0 ? <FaCheckCircle /> : <FaTimesCircle />}
          </div>
          <div className="result-details">
            <h4>Promotion Complete</h4>
            <p>
              <strong>{promotionResult.success}</strong> of <strong>{promotionResult.total}</strong> students 
              promoted from <strong>{promotionResult.fromClass}</strong> to <strong>{promotionResult.toClass}</strong>
            </p>
            {promotionResult.failed > 0 && (
              <p className="result-failed">{promotionResult.failed} student(s) failed to promote</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default StudentPromotion;
